// src/plugins/sendbird/sendbird-client.js
import SendbirdChat, { ConnectionHandler } from '@sendbird/chat';
import {
  GroupChannelModule,
  GroupChannelHandler,
  MessageFilter,
} from '@sendbird/chat/groupChannel';

import { channelType } from '@/plugins/globals.js';

import { ref, nextTick } from 'vue';
import { xssEscape } from '@/plugins/utils';
import cloneDeep from 'lodash.clonedeep';
import store from '@/store';
import { getSendbirdToken } from '@/plugins/sendbird/sendbird-token';
import { getI18nInstance } from '@/plugins/i18n';
import { getAppId } from '@/plugins/sendbird/portal-appId';
import { GLOBAL_HANDLER, GET_MESSAGE_LIMIT, MAX_COUNT } from '@/store/modules/sendbird/types';

const SENDBIRD_APP_ID = getSendbirdToken();
const APP_ID = getAppId();

let sbInstance = null;
let _connectInFlight = null;

const initialized = ref(false);
let hasHydratedUnread = false;

let __pendingDing = false;

// Single active channel per tab
const activeChannel = ref(null);
const viewingUrl = ref(null);

// Active MessageCollection state for the open chat
const activeCollection = ref(null);
const activeCollectionUrl = ref(null);

// In-memory snapshots by channel url so existing callers can keep using getMessageList()
const recentSnapshots = new Map();

// prevent overlapping markAsRead calls per channel
const readInFlightByUrl = new Map();

const isViewingChannel = (ch) => {
  const url = ch?.url || ch;
  return !!url && viewingUrl.value === url;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getCurrentUserId = () =>
  store.getters['auth/chatIdentityEmail'] ||
  store.getters['auth/userProfile']?.email ||
  '';

const getCurrentNickname = () => {
  const chatAs = store.getters['auth/chatAs'];
  const profile = store.getters['auth/userProfile'];
  const first = chatAs?.firstName || profile?.firstName || '';
  const last = chatAs?.lastName || profile?.lastName || '';
  const fullName = [first, last].filter(Boolean).join(' ').trim();
  return fullName || first || last || getCurrentUserId() || 'User';
};

async function getChannelWithRetry(url, { tries = 5, delayMs = 120 } = {}) {
  if (!sbInstance) throw new Error('Sendbird not initialized');
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      return await sbInstance.groupChannel.getChannel(url);
    } catch (e) {
      const code = e?.code || 0;
      if (code === 400 || code === 400404) {
        lastErr = e;
        await sleep(delayMs * (i + 1));
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error('getChannelWithRetry: exhausted retries');
}

// === ALWAYS-ON DEBUG BIND ===
if (typeof window !== 'undefined' && !window.__sbBound) {
  window.__sbBound = true;

  Object.defineProperty(window, '__sb', { get: () => sbInstance });

  window.__sbUser = () => sbInstance?.currentUser?.userId || null;

  window.__sbGet = async (url) => {
    if (!sbInstance) throw new Error('Sendbird not initialized yet');
    const ch = await sbInstance.groupChannel.getChannel(url);
    if (typeof ch.refresh === 'function') {
      try { await ch.refresh(); } catch {}
    }
    return ch;
  };

  window.__sbCount = async (url) => {
    const ch = await window.__sbGet(url);
    return Number(ch.unreadMessageCount || 0);
  };

  window.__sbMy = async (url) => {
    if (!sbInstance) throw new Error('Sendbird not initialized yet');
    const q = sbInstance.groupChannel.createMyGroupChannelListQuery({
      includeEmpty: true,
      channelUrlsFilter: [url],
    });
    const list = await q.next();
    return list?.[0] ?? null;
  };

  window.__badge = async (idOrUrl) => {
    const url = String(idOrUrl).startsWith('group_channel_')
      ? String(idOrUrl)
      : `group_channel_${idOrUrl}`;
    const ch = await window.__sbGet(url);
    console.log('[SENDBIRD-DBG] __badge -> url:', url, 'unreadMessageCount:', ch?.unreadMessageCount);
    return ch?.unreadMessageCount ?? null;
  };

  console.log('[SENDBIRD-DBG] helpers bound (lazy). current user:', window.__sbUser());
}

const playChatDing = () => {
  const audioEl = document.getElementById('chatNotifier');
  if (!audioEl || typeof audioEl.play !== 'function') return;

  audioEl.play().catch(() => {
    if (__pendingDing) return;
    __pendingDing = true;
    const resume = () => {
      __pendingDing = false;
      audioEl.play().catch(() => {});
      window.removeEventListener('pointerdown', resume, { capture: true });
      window.removeEventListener('keydown', resume, { capture: true });
    };
    window.addEventListener('pointerdown', resume, { once: true, capture: true });
    window.addEventListener('keydown', resume, { once: true, capture: true });
  });
};

const normalizeLang = (l) => {
  if (!l) return 'en';
  const lc = l.toLowerCase();
  if (lc === 'gr') return 'el';
  return lc.split('-')[0];
};

const getUiLang = () => {
  try {
    const i18n = getI18nInstance?.();
    const fromI18n =
      i18n?.global?.locale?.value ??
      i18n?.global?.locale ??
      null;

    if (fromI18n) return normalizeLang(fromI18n);
  } catch {}

  const fromDom = document?.documentElement?.lang;
  if (fromDom) return normalizeLang(fromDom);

  const fromNav = navigator?.language;
  if (fromNav) return normalizeLang(fromNav);

  return 'en';
};

const normalizeIncomingMessage = async (m) => {
  if (!m) return m;

  const msg = cloneDeep(m);

  // sender normalization
  if (!msg.sender && msg._sender) {
    msg.sender = msg._sender;
  }

  // timestamp normalization
  if (msg.created_at != null && msg.createdAt == null) {
    msg.createdAt = msg.created_at;
  }
  if (msg.ts != null && msg.createdAt == null) {
    msg.createdAt = msg.ts;
  }

  const ts = Number(msg.createdAt);
  if (!Number.isFinite(ts) || ts <= 0) {
    msg.createdAt = Date.now();
  }

  // auth flag normalization
  if (msg.requireAuth == null && msg.require_auth != null) {
    msg.requireAuth = msg.require_auth;
  }

  // normalize file name
  if (!msg.name) {
    msg.name =
      msg.fileName ||
      msg.filename ||
      (msg.file && (msg.file.name || msg.file.filename)) ||
      '';
  }

  // normalize mime type
  if (!msg.type) {
    msg.type =
      msg.mimeType ||
      msg.contentType ||
      (msg.file && (msg.file.type || msg.file.mimeType || msg.file.contentType)) ||
      '';
  }

  if (!msg.mimeType) {
    msg.mimeType = msg.type || '';
  }

  if (!msg.contentType) {
    msg.contentType = msg.type || '';
  }

  // normalize messageType for safety
  if (!msg.messageType) {
    if (typeof msg.isUserMessage === 'function' && msg.isUserMessage()) {
      msg.messageType = 'user';
    } else if (typeof msg.isFileMessage === 'function' && msg.isFileMessage()) {
      msg.messageType = 'file';
    } else if (typeof msg.isAdminMessage === 'function' && msg.isAdminMessage()) {
      msg.messageType = 'admin';
    }
  }

  // raw URL candidates
  const rawUrl =
    msg.url ||
    msg.plainUrl ||
    msg.secureUrl ||
    msg.fileUrl ||
    (msg.file && (msg.file.url || msg.file.localUrl)) ||
    '';

  // public file path
  if (!msg.plainUrl && rawUrl && !msg.requireAuth) {
    msg.plainUrl = rawUrl;
  }

  // signed/authenticated file path
  if (!msg.plainUrl && rawUrl) {
    try {
      if (typeof sbInstance?.getFileUrl === 'function') {
        msg.plainUrl = await sbInstance.getFileUrl(rawUrl);
      } else if (typeof sbInstance?.createFileUrl === 'function') {
        msg.plainUrl = await sbInstance.createFileUrl(rawUrl);
      } else if (typeof sbInstance?.auth?.getFileUrl === 'function') {
        msg.plainUrl = await sbInstance.auth.getFileUrl(rawUrl);
      }
    } catch (e) {
      console.warn('[sendbird-client] normalizeIncomingMessage failed to resolve file URL:', e);
    }
  }

  return msg;
};

const normalizeIncomingMessages = async (messages = []) => {
  return await Promise.all(
    (Array.isArray(messages) ? messages : []).map((m) => normalizeIncomingMessage(m))
  );
};

const sortMessages = (list) => {
  return (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => Number(a?.createdAt || 0) - Number(b?.createdAt || 0));
};

const sameSender = (a, b) => {
  const aSender = String(a?.sender?.userId || a?._sender?.userId || a?.userId || '').toLowerCase();
  const bSender = String(b?.sender?.userId || b?._sender?.userId || b?.userId || '').toLowerCase();
  return aSender && bSender && aSender === bSender;
};

const sameText = (a, b) => {
  return String(a?.message || '').trim() &&
    String(a?.message || '').trim() === String(b?.message || '').trim();
};

const closeTimestamp = (a, b, toleranceMs = 120000) => {
  const aTs = Number(a?.createdAt || 0);
  const bTs = Number(b?.createdAt || 0);
  return aTs > 0 && bTs > 0 && Math.abs(aTs - bTs) <= toleranceMs;
};

const hasRealMessageId = (m) => {
  const id = m?.messageId
  if (id == null) return false

  const s = String(id)
  if (!s || s.startsWith('local-preview-') || s.startsWith('preview-')) return false

  return Number(id) > 0
}

const sameLogicalMessage = (a, b) => {
  if (!a || !b) return false

  const aMessageId = a?.messageId ? String(a.messageId) : ''
  const bMessageId = b?.messageId ? String(b.messageId) : ''

  if (hasRealMessageId(a) && hasRealMessageId(b)) {
    return aMessageId === bMessageId
  }

  if (aMessageId && bMessageId && aMessageId === bMessageId) {
    return true
  }

  const aReqId = a?.reqId || a?.req_id || a?.requestId || ''
  const bReqId = b?.reqId || b?.req_id || b?.requestId || ''

  if (aReqId && bReqId) {
    return String(aReqId) === String(bReqId)
  }

  // Only fuzzy-match when at least one side is not a confirmed Sendbird server message.
  // This prevents repeated real messages with the same text being collapsed.
  if (!hasRealMessageId(a) || !hasRealMessageId(b)) {
    return sameSender(a, b) && sameText(a, b) && closeTimestamp(a, b, 30000)
  }

  return false
}

const mergeMessages = (existing = [], incoming = []) => {
  const merged = [...(Array.isArray(existing) ? existing : [])];

  for (const nextMessage of Array.isArray(incoming) ? incoming : []) {
    const index = merged.findIndex((currentMessage) =>
      sameLogicalMessage(currentMessage, nextMessage)
    );

    if (index >= 0) {
      merged[index] = {
        ...merged[index],
        ...nextMessage,
      };
    } else {
      merged.push(nextMessage);
    }
  }

  return sortMessages(merged);
};

const writeSnapshot = async (channelUrl, messages, { replace = false } = {}) => {
  const safeUrl = String(channelUrl || '').trim();
  if (!safeUrl) return;

  const normalizedInput = await normalizeIncomingMessages(messages || []);
  const normalized = sortMessages(normalizedInput);

  const next = replace
    ? normalized
    : mergeMessages(recentSnapshots.get(safeUrl) || [], normalized);

  recentSnapshots.set(safeUrl, next);

  store.commit('sendbird/SET_RECENT_MESSAGES_REPLACE', {
    channelUrl: safeUrl,
    messages: cloneDeep(next),
  });
};

const appendLiveMessagesToSnapshot = async (channelUrl, messages = []) => {
  const safeUrl = String(channelUrl || '').trim()
  if (!safeUrl) return

  const normalized = await normalizeIncomingMessages(
    Array.isArray(messages) ? messages : [messages]
  )

  await writeSnapshot(safeUrl, normalized, { replace: false })
};

const deleteFromSnapshot = (channelUrl, messageIds = []) => {
  const safeUrl = String(channelUrl || '').trim();
  if (!safeUrl) return;

  const ids = new Set((Array.isArray(messageIds) ? messageIds : [messageIds]).map(String));
  const current = recentSnapshots.get(safeUrl) || [];
  const next = current.filter((m) => !ids.has(String(m?.messageId)));

  recentSnapshots.set(safeUrl, next);

  store.commit('sendbird/SET_RECENT_MESSAGES_REPLACE', {
    channelUrl: safeUrl,
    messages: cloneDeep(next),
  });
};

const getSnapshot = (channelUrl) => {
  return cloneDeep(recentSnapshots.get(String(channelUrl || '').trim()) || []);
};

const disposeActiveCollection = () => {
  try {
    activeCollection.value?.dispose?.();
  } catch {}
  activeCollection.value = null;
  activeCollectionUrl.value = null;
};

const fallbackFetchRecentMessages = async (channel, limit = GET_MESSAGE_LIMIT) => {
  if (!channel) return [];
  const query = channel.createPreviousMessageListQuery();
  const batch = await query.load(limit, false);
  return await normalizeIncomingMessages(sortMessages(batch || []));
};

export const setChatOpen = (isOpen, url = null) => {
  if (isOpen) {
    const u = url ?? activeChannel.value?.url ?? null;
    viewingUrl.value = u;
    console.log('[SENDBIRD-DBG] setChatOpen(true) viewingUrl =', u);
  } else {
    viewingUrl.value = null;
    console.log('[SENDBIRD-DBG] setChatOpen(false) viewingUrl cleared');
  }
};

export const getViewingUrl = () => viewingUrl.value || null;
export const setHasHydratedUnread = (v) => { hasHydratedUnread = !!v; };

export const useSendbird = () => {
  const initSendbird = async () => {
    if (!sbInstance) {
      sbInstance = await SendbirdChat.init({
        appId: SENDBIRD_APP_ID,
        modules: [new GroupChannelModule()],
      });
      initialized.value = true;
    }
    return sbInstance;
  };

  const getCurrentUser = () => {
    return sbInstance?.currentUser || null;
  };

  const RECONCILE_LIMIT = 50;
  const _reconcileTimers = new Map();

  const reconcileRecent = async (channelUrl, limit = RECONCILE_LIMIT) => {
    try {
      const ch = await getChannelWithRetry(channelUrl, { tries: 3, delayMs: 120 });
      const list = await fallbackFetchRecentMessages(ch, limit);
      await writeSnapshot(channelUrl, list, { replace: true });
    } catch (e) {
      console.warn('[sendbird] reconcileRecent failed:', e);
    }
  };

  const scheduleReconcile = (channelUrl, delay = 400) => {
    clearTimeout(_reconcileTimers.get(channelUrl));
    const t = setTimeout(() => {
      reconcileRecent(channelUrl).catch(() => {});
    }, delay);
    _reconcileTimers.set(channelUrl, t);
  };

  const requestIdFromChannel = (ch) => {
    try {
      const url = ch?.url || '';
      if (url.startsWith('group_channel_')) return url.slice('group_channel_'.length);
    } catch {}
    return String(ch?.name ?? '');
  };

  const isSafeChannelUrl = (url) => {
    return (
      typeof url === 'string' &&
      url.length <= 128 &&
      /^[a-zA-Z0-9-_]+$/.test(url) &&
      url.startsWith('group_channel_')
    );
  };

  const getUnreadCountByUrls = async (urls = []) => {
    const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
    const zeroMap = Object.fromEntries(list.map(u => [u, 0]));

    if (!sbInstance || !sbInstance.currentUser || !sbInstance.currentUser.userId) {
      console.warn('[sendbird] getUnreadCountByUrls: not connected; returning 0s.');
      return zeroMap;
    }

    if (!list.length) return {};

    try {
      const q = sbInstance.groupChannel.createMyGroupChannelListQuery({
        includeEmpty: true,
        limit: list.length,
        channelUrlsFilter: list,
      });
      const serverList = await q.next();
      const map = { ...zeroMap };
      for (const ch of serverList || []) {
        map[ch.url] = Number(ch.unreadMessageCount || 0);
      }
      return map;
    } catch (e) {
      console.warn('[sendbird] getUnreadCountByUrls error:', e);
      return zeroMap;
    }
  };

  const sendbirdConnect = async ({ userId, nickname }) => {
    const desiredUserId = String(userId || '').trim();
    const desiredNickname = String(nickname || userId || '').trim();
    if (!desiredUserId) throw new Error('Missing userId');

    if (_connectInFlight) {
      try { await _connectInFlight; } catch {}
    }

    _connectInFlight = (async () => {
      await initSendbird();

      if (sbInstance?.currentUser?.userId === desiredUserId) {
        try {
          const uiLang = getUiLang();
          if ((sbInstance.currentUser.nickname || '') !== desiredNickname) {
            await sbInstance.updateCurrentUserInfo({
              nickname: desiredNickname,
              preferredLanguages: [uiLang, 'en'],
            });
          }
        } catch {}
        attachGlobalChannelHandler();
        return sbInstance.currentUser;
      }

      try { await sbInstance?.disconnect(); } catch {}
      disposeActiveCollection();
      sbInstance = null;
      await initSendbird();

      const user = await sbInstance.connect(desiredUserId);

      try {
        const uiLang = getUiLang();
        if ((user.nickname || '') !== desiredNickname) {
          await sbInstance.updateCurrentUserInfo({
            nickname: desiredNickname,
            preferredLanguages: [uiLang, 'en'],
          });
        }
      } catch {}

      attachGlobalChannelHandler();
      return user;
    })();

    try {
      return await _connectInFlight;
    } finally {
      _connectInFlight = null;
    }
  };

  const disconnect = async () => {
    disposeActiveCollection();

    if (sbInstance) {
      await sbInstance.disconnect();
      sbInstance = null;
      activeChannel.value = null;
      initialized.value = false;
      recentSnapshots.clear();
      readInFlightByUrl.clear();
    }
  };

  // ===== DEBUG HELPERS =====
  const DEBUG_CHAT = true;
  window.__SB_LOGS = window.__SB_LOGS || [];
  let __evSeq = 0;
  const evId = () => (++__evSeq).toString().padStart(4, '0');

  const dbg = (...args) => {
    if (!DEBUG_CHAT) return;
    try {
      const line = ['[SENDBIRD-DBG]', ...args];
      console.log(...line);
      window.__SB_LOGS.push(
        line.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(' ')
      );
      if (window.__SB_LOGS.length > 1000) window.__SB_LOGS.splice(0, 200);
    } catch {}
  };

  window.showSbLogs = () => {
    console.log('[SENDBIRD-DBG] Dumping __SB_LOGS', window.__SB_LOGS.length);
    window.__SB_LOGS.forEach((l, i) => console.log(String(i + 1).padStart(4, '0'), l));
  };

  const chKey = (ch) => {
    try {
      const url = ch?.url || ch;
      const rid = requestIdFromChannel?.(ch) || '??';
      return `${url} (reqId:${rid})`;
    } catch {
      return String(ch?.url || ch);
    }
  };

  const getStoreCountFor = (reqId) => {
    try {
      const fn = store.getters['serviceRequests/unreadForRequest'];
      return typeof fn === 'function' ? Number(fn(reqId) || 0) : NaN;
    } catch {
      return NaN;
    }
  };

  const updateUnreadWithLog = async ({ id, count, source, extra = {} }) => {
    const before = getStoreCountFor(id);
    dbg(`EV#${extra.ev || '----'} updateUnread -> id:${id} from:${source} before:${before} -> ${count}`, extra);
    await store.dispatch('serviceRequests/updateRequestUnread', { id, count });
    const after = getStoreCountFor(id);
    dbg(`EV#${extra.ev || '----'} updateUnread DONE id:${id} after:${after}`);
  };

  window.__probe = async (idOrUrl) => {
    const url = String(idOrUrl).startsWith('group_channel_')
      ? String(idOrUrl)
      : `group_channel_${idOrUrl}`;

    const out = { url };

    try {
      const ch = await window.__sbGet(url);
      try { await ch.refresh?.(); } catch {}
      out.memberState = ch?.myMemberState ?? 'unknown';
      out.sdkUnread_afterRefresh = Number(ch?.unreadMessageCount ?? NaN);
    } catch (e) {
      out.getChannelError = e?.message || String(e);
    }

    try {
      const q = window.__sb.groupChannel.createMyGroupChannelListQuery({
        includeEmpty: true,
        limit: 1,
        channelUrlsFilter: [url],
      });
      const list = await q.next();
      const mine = list?.[0] || null;
      out.myListQuery_found = !!mine;
      out.myListQuery_unread = mine ? Number(mine.unreadMessageCount || 0) : 'n/a';
      out.myListQuery_memberState = mine?.myMemberState || 'n/a';
    } catch (e) {
      out.myListQuery_error = e?.message || String(e);
    }

    console.log('[SENDBIRD-DBG] __probe', out);
    return out;
  };

  const getMyChannelByUrl = async (channelUrl) => {
    if (!isSafeChannelUrl(channelUrl)) return null;
    if (!sbInstance?.currentUser?.userId) return null;

    const run = async () => {
      const q = sbInstance.groupChannel.createMyGroupChannelListQuery({
        includeEmpty: true,
        limit: 10,
        channelUrlsFilter: [channelUrl],
      });
      const list = await q.next();
      return Array.isArray(list) && list.length ? list[0] : null;
    };

    try {
      return await run();
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes("Session-Key doesn't match user_id")) {
        try { await sbInstance?.disconnect(); } catch {}
        disposeActiveCollection();
        sbInstance = null;

        const userId = getCurrentUserId();
        const nickname = getCurrentNickname();
        if (userId) {
          await sendbirdConnect({ userId, nickname });
          try { return await run(); } catch {}
        }
      }
      console.warn('[sendbird] getMyChannelByUrl failed:', e);
      return null;
    }
  };

  const markChannelRead = async (target) => {
    const url = (() => {
      if (typeof target === 'string') return target;
      return target?.url || null;
    })();

    if (!url) return;

    if (readInFlightByUrl.get(url)) {
      return;
    }

    readInFlightByUrl.set(url, true);

    try {
      const ch = typeof target === 'string'
        ? await getMyChannelByUrl(target)
        : target;

      const derivedId = (() => {
        if (ch?.url && ch.url.startsWith('group_channel_')) {
          return ch.url.slice('group_channel_'.length);
        }
        if (typeof target === 'string' && target.startsWith('group_channel_')) {
          return target.slice('group_channel_'.length);
        }
        const n = Number(ch?.name);
        return Number.isFinite(n) && n > 0 ? String(n) : null;
      })();

      if (!ch || ch.myMemberState !== 'joined') {
        if (derivedId) {
          await store.dispatch('serviceRequests/updateRequestUnread', {
            id: derivedId,
            count: 0,
          });
        }
        return;
      }

      await ch.markAsRead();

      const map = await getUnreadCountByUrls([ch.url]);
      const serverCount = Number(map?.[ch.url] || 0);

      if (derivedId) {
        await store.dispatch('serviceRequests/updateRequestUnread', {
          id: derivedId,
          count: serverCount,
        });
      }
    } catch (e) {
      console.warn('[sendbird-client] markChannelRead failed, falling back to 0:', e);

      const derivedId = (() => {
        if (typeof target === 'string' && target.startsWith('group_channel_')) {
          return target.slice('group_channel_'.length);
        }
        const targetUrl = target?.url;
        if (targetUrl && targetUrl.startsWith('group_channel_')) return targetUrl.slice('group_channel_'.length);
        const n = Number(target?.name);
        return Number.isFinite(n) && n > 0 ? String(n) : null;
      })();

      if (derivedId) {
        await store.dispatch('serviceRequests/updateRequestUnread', {
          id: derivedId,
          count: 0,
        });
      }
    } finally {
      readInFlightByUrl.delete(url);
    }
  };

  const attachCollectionHandlers = (collection) => {
    collection.setMessageCollectionHandler({
      onMessagesAdded: async (context, ch, messages) => {
        console.log('DEBUG => [CLIENT COLLECTION.onMessagesAdded]', {
          channel: ch.url,
          count: messages?.length || 0,
          mids: (messages || []).map(m => m?.messageId ?? m?.reqId ?? null),
        });

        const normalized = await normalizeIncomingMessages(messages || []);
        await writeSnapshot(ch.url, normalized, { replace: false });

        await nextTick();
        const el = document.getElementById('chatMessages');
        if (el) el.scrollTop = el.scrollHeight;
      },

      onMessagesUpdated: async (context, ch, messages) => {
        console.log('DEBUG => [CLIENT COLLECTION.onMessagesUpdated]', {
          channel: ch.url,
          count: messages?.length || 0,
          mids: (messages || []).map(m => m?.messageId ?? m?.reqId ?? null),
        });

        const normalized = await normalizeIncomingMessages(messages || []);
        await writeSnapshot(ch.url, normalized, { replace: false });

        await nextTick();
        const el = document.getElementById('chatMessages');
        if (el) el.scrollTop = el.scrollHeight;
      },

      onMessagesDeleted: async (context, ch, messageIds) => {
        const ids =
          Array.isArray(messageIds) && messageIds.length
            ? messageIds
            : Array.isArray(context?.messageIds) && context.messageIds.length
              ? context.messageIds
              : Array.isArray(context?.deletedMessageIds) && context.deletedMessageIds.length
                ? context.deletedMessageIds
                : [];

        console.log('DEBUG => [CLIENT COLLECTION.onMessagesDeleted]', {
          channel: ch?.url,
          ids,
          context,
        });

        if (ids.length > 0) {
          deleteFromSnapshot(ch.url, ids);
          return;
        }

        // Fallback: admin/control-panel deletes may not always provide IDs reliably.
        // Pull the latest server state so the UI removes deleted messages without Refresh.
        try {
          const recent = await fallbackFetchRecentMessages(ch, GET_MESSAGE_LIMIT);
          await writeSnapshot(ch.url, recent, { replace: true });
        } catch (e) {
          console.warn('[sendbird-client] onMessagesDeleted fallback refresh failed:', e);
        }
      },

      onChannelUpdated: async (context, ch) => {
        if (!isViewingChannel(ch)) return;
        if (!ch?.unreadMessageCount) return;

        try {
          await markChannelRead(ch);
        } catch {}
      },

      onChannelDeleted: (context, deletedChannelUrl) => {
        if (deletedChannelUrl === activeChannel.value?.url) {
          activeChannel.value = null;
        }
        if (deletedChannelUrl === activeCollectionUrl.value) {
          disposeActiveCollection();
        }
      },

      onHugeGapDetected: async () => {
        const ch = activeChannel.value;
        const url = activeCollectionUrl.value;
        console.warn('[sendbird-client] MessageCollection huge gap detected for', url);

        disposeActiveCollection();

        if (ch && url && ch.url === url) {
          await openMessageCollection(ch);
        }
      },
    });
  };

  const openMessageCollection = async (channel) => {
    if (!channel) throw new Error('Missing channel');

    const url = String(channel.url || '').trim();
    if (!url) throw new Error('Missing channel url');

    if (activeCollection.value && activeCollectionUrl.value === url) {
      return activeCollection.value;
    }

    disposeActiveCollection();

    const filter = new MessageFilter();
    const collection = channel.createMessageCollection({
      filter,
      prevResultLimit: 30,
      nextResultLimit: 0,
      startingPoint: Date.now(),
    });

    attachCollectionHandlers(collection);

    activeCollection.value = collection;
    activeCollectionUrl.value = url;

    let settled = false;
    let cacheSeen = false;
    let apiSeen = false;

    const finish = async ({ preferFallback = false } = {}) => {
      if (settled) return;
      settled = true;

      const current = getSnapshot(url);
      if (current.length > 0 && !preferFallback) return;

      try {
        const fallback = await fallbackFetchRecentMessages(channel, GET_MESSAGE_LIMIT);
        const normalizedFallback = await normalizeIncomingMessages(sortMessages(fallback || []));
        await writeSnapshot(url, normalizedFallback, { replace: true });
      } catch (e) {
        console.warn('[sendbird-client] fallbackFetchRecentMessages failed during collection open:', e);
      }
    };

    await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(async () => {
        try {
          await finish({ preferFallback: true });
          resolve();
        } catch (e) {
          reject(e);
        }
      }, 1800);

      try {
        collection
          .initialize('CACHE_AND_REPLACE_BY_API')
          .onCacheResult(async (err, messages) => {
            if (err) {
              console.warn('[sendbird-client] collection cache init failed:', err);
              return;
            }

            cacheSeen = true;

            const list = await normalizeIncomingMessages(sortMessages(messages || []));
            await writeSnapshot(url, list, { replace: true });

            if (list.length > 0 && !settled) {
              settled = true;
              clearTimeout(timeoutId);
              resolve();
            }
          })
          .onApiResult(async (err, messages) => {
            if (err) {
              console.error('[sendbird-client] collection api init failed:', err);
              if (!settled) {
                clearTimeout(timeoutId);
                settled = true;
                reject(err);
              }
              return;
            }

            apiSeen = true;

            const list = await normalizeIncomingMessages(sortMessages(messages || []));
            await writeSnapshot(url, list, { replace: true });

            if (!settled) {
              settled = true;
              clearTimeout(timeoutId);
              resolve();
            }
          });
      } catch (e) {
        reject(e);
      }
    }).catch(async (err) => {
      console.warn('[sendbird-client] openMessageCollection init failed; using fallback query:', err);
      try {
        await finish({ preferFallback: true });
      } catch {}
    });

    if (!apiSeen && cacheSeen && getSnapshot(url).length === 0) {
      try {
        const fallback = await fallbackFetchRecentMessages(channel, GET_MESSAGE_LIMIT);
        const normalizedFallback = await normalizeIncomingMessages(sortMessages(fallback || []));
        await writeSnapshot(url, normalizedFallback, { replace: true });
      } catch {}
    }

    return collection;
  };

  const hydrateBadgeForRequest = async (reqId, ev = 'HYDR') => {
    if (!reqId) return;
    const url = `group_channel_${reqId}`;

    try {
      const mine = await getMyChannelByUrl(url);
      if (!mine) {
        await updateUnreadWithLog({
          id: String(reqId),
          count: 0,
          source: 'hydrate:not-member',
          extra: { ev }
        });
        return;
      }

      try { await mine.refresh?.(); } catch {}

      const sdk = Number(mine.unreadMessageCount || 0);

      let bulk = null;
      try {
        const m = await getUnreadCountByUrls([url]);
        bulk = Number(m?.[url] ?? 0);
      } catch {}

      await updateUnreadWithLog({
        id: String(reqId),
        count: sdk,
        source: bulk === null ? 'hydrate:sdk' : (sdk !== bulk ? 'hydrate:sdk!=bulk' : 'hydrate:sdk'),
        extra: { ev, sdk, bulk }
      });
    } catch (e) {
      await updateUnreadWithLog({
        id: String(reqId),
        count: 0,
        source: 'hydrate:fallback',
        extra: { ev, err: e?.message }
      });
    }
  };

  const attachGlobalChannelHandler = () => {
    if (!sbInstance) return;

    const handler = new GroupChannelHandler();

    handler.onMessageReceived = async (channel, message) => {
      const ev = evId();
      const reqId = requestIdFromChannel(channel);
      const isViewing = isViewingChannel(channel);
      const channelUrl = channel.url;
      const sdkCount = Number(channel.unreadMessageCount || 0);

      const me =
        sbInstance?.currentUser?.userId ||
        store.getters['auth/chatIdentityEmail'] ||
        store.getters['auth/userProfile']?.email ||
        '';

      const senderId = message?.sender?.userId || message?._sender?.userId || '';

      const isOwnMessage =
        me &&
        senderId &&
        String(me).toLowerCase() === String(senderId).toLowerCase();

      if (isOwnMessage) {
        console.log('DEBUG => [GLOBAL.onMessageReceived] own message ignored to prevent duplicate', {
          channel: channel.url,
          mid: message?.messageId ?? message?.reqId ?? null,
        });
        return;
      }
      const collectionOwnsThisChannel =
        !!activeCollection.value && activeCollectionUrl.value === channelUrl;

      if (!collectionOwnsThisChannel) {
        const uiLang = getUiLang();
        const languages = [uiLang, 'en'];

        try {
          if (message?.messageType === 'user') {
            const translated = await channel.translateUserMessage(message, languages);
            const normalizedMessage = await normalizeIncomingMessage(message);
            const cloned = cloneDeep(normalizedMessage);
            const target = languages[0];
            cloned.message =
              translated?.translations?.[target] ??
              translated?.translations?.[target?.split('-')[0]] ??
              translated?.message ??
              cloned.message;

            await store.dispatch('sendbird/addLiveMessage', {
              channelUrl,
              message: cloned,
            });
          } else {
            const normalizedMessage = await normalizeIncomingMessage(message);
            await store.dispatch('sendbird/addLiveMessage', {
              channelUrl,
              message: normalizedMessage,
            });
          }
        } catch {
          const normalizedMessage = await normalizeIncomingMessage(message);
          await store.dispatch('sendbird/addLiveMessage', {
            channelUrl,
            message: normalizedMessage,
          });
        }

        scheduleReconcile(channelUrl, 350);
      } else {
        console.log('DEBUG => [GLOBAL.messageReceivedAction] collection-owned fallback append', {
          channel: channel.url,
          mid: message?.messageId ?? null,
        })

        await appendLiveMessagesToSnapshot(channel.url, [message])
      }

      const chatDialog = document.getElementById('rightSlideOut');
      const panelClosed = !chatDialog || chatDialog.style.display === 'none';
      if (panelClosed && document.visibilityState === 'visible') {
        playChatDing();
      }

      dbg(`EV#${ev} onMessageReceived ${chKey(channel)} viewing:${isViewing} sdkCount:${sdkCount}`);

      if (isViewing) {
        await updateUnreadWithLog({
          id: reqId,
          count: 0,
          source: 'msg:viewing-zero',
          extra: { ev }
        });

        await markChannelRead(channel);
      } else {
        const nextCount = Math.max(
          Number(channel.unreadMessageCount || 0),
          Number(getStoreCountFor(reqId) || 0) + 1
        );
        await updateUnreadWithLog({
          id: reqId,
          count: nextCount,
          source: 'msg:local-or-sdk',
          extra: { ev, sdkCount }
        });
      }

      store.dispatch('sendbird/audit', {
        source: 'Portal3',
        userIds: message.sender?.userId,
        channel: message.channelUrl,
        details: `(onMessageReceived) ${message.message || message.name || ''}`,
      });

      await nextTick();
      const el = document.getElementById('chatMessages');
      if (el && !collectionOwnsThisChannel) el.scrollTop = el.scrollHeight;
    };

    handler.onMessageDeleted = async (channel, messageId) => {
      console.log('DEBUG => [GLOBAL.onMessageDeleted]', {
        channel: channel?.url,
        messageId,
      });

      if (messageId) {
        deleteFromSnapshot(channel.url, [messageId]);
        return;
      }

      try {
        const recent = await fallbackFetchRecentMessages(channel, GET_MESSAGE_LIMIT);
        await writeSnapshot(channel.url, recent, { replace: true });
      } catch (e) {
        console.warn('[sendbird-client] global onMessageDeleted fallback refresh failed:', e);
      }
    };

    handler.onTypingStatusUpdated = (channel) => {
      if (channel.url === activeChannel.value?.url) {
        store.dispatch('sendbird/setTyping', channel);
      }
    };

    handler.onChannelChanged = async (channel) => {
      if (!hasHydratedUnread) return;
      const ev = evId();
      const isActive = isViewingChannel(channel);
      const reqId = requestIdFromChannel(channel);
      const sdkCount = Number(channel.unreadMessageCount || 0);
      const isViewing = channel.url === viewingUrl.value;

      dbg(`EV#${ev} onChannelChanged ${chKey(channel)} active:${isActive} sdkCount:${sdkCount}`);

      if (isViewing) return;

      await updateUnreadWithLog({
        id: reqId,
        count: isActive ? 0 : sdkCount,
        source: 'changed:sdkCount',
        extra: { ev }
      });
    };

    handler.onChannelDeleted = (channelUrl) => {
      console.log(`DEBUG => [Sendbird-client=>handler.onChannelDeleted] channelUrl: ${channelUrl}, APP_ID: ${APP_ID}`);
      if (channelUrl === activeChannel.value?.url) {
        activeChannel.value = null;
      }

      if (channelUrl === activeCollectionUrl.value) {
        disposeActiveCollection();
      }

      if (APP_ID === 4) return;

      const name = String(channelUrl || '');
      if (!name.startsWith('group_channel_')) return;

      const request = name.replace('group_channel_', '').trim();
      const workOrderId = Number.parseInt(request, 10);

      if (Number.isInteger(workOrderId) && workOrderId > 0) {
        localStorage.setItem('survey_pending_workOrderId', String(workOrderId));
        localStorage.setItem('survey_pending_setAt', String(Date.now()));
      }

      console.log(`DEBUG => [Sendbird-client=>handler.onChannelDeleted] Channel deleted: ${channelUrl}`);

      alert(`Service Request #${request} has been Completed.`);
      window.location.assign('/');
    };

    try { sbInstance.groupChannel.removeGroupChannelHandler(GLOBAL_HANDLER); } catch {}
    sbInstance.groupChannel.addGroupChannelHandler(GLOBAL_HANDLER, handler);

    const connHandler = new ConnectionHandler();
    connHandler.onReconnectSucceeded = async () => {
      const url = activeChannel.value?.url || null;
      if (!url) return;
      try {
        const ch = await getChannelWithRetry(url, { tries: 3, delayMs: 100 });

        if (isViewingChannel(ch) && ch?.unreadMessageCount > 0) {
          await markChannelRead(ch);
        }

        const reqId = requestIdFromChannel(ch);
        store.dispatch('serviceRequests/updateRequestUnread', {
          id: reqId,
          count: Number(ch.unreadMessageCount || 0),
        });

        if (activeChannel.value?.url === url) {
          await openMessageCollection(ch);
        } else {
          scheduleReconcile(url, 0);
        }
      } catch {}
    };

    try { sbInstance.removeConnectionHandler?.('PORTAL_SYNC'); } catch {}
    sbInstance.addConnectionHandler('PORTAL_SYNC', connHandler);
  };

  const finalizeReadAndClose = async (urlOrChannel = null) => {
    try {
      const target = urlOrChannel ?? (activeChannel.value || viewingUrl.value || null);
      if (target) {
        await markChannelRead(target);
      }
    } finally {
      setChatOpen(false);
      disposeActiveCollection();
    }
  };

  const setActiveChannel = (channel) => {
    activeChannel.value = channel;
  };

  const getActiveChannel = () => activeChannel.value;

  const getChannelInfo = (channel) => {
    if (!channel) return null;

    const memberCount = channel.memberCount > 9 ? MAX_COUNT : channel.memberCount.toString();
    const memberList = channel.members
      .filter((m) => m.userId !== store.getters['auth/userProfile']?.email)
      .map((m) => xssEscape(m.nickname));

    return {
      channel,
      memberCount,
      memberList,
    };
  };

  const refreshUnreadForUrl = async (url, idHint = null) => {
    try {
      const ch = await getChannelWithRetry(url, { tries: 3, delayMs: 100 });
      if (!ch) return;
      const reqId = idHint || requestIdFromChannel(ch);
      await store.dispatch('serviceRequests/updateRequestUnread', {
        id: String(reqId),
        count: Number(ch.unreadMessageCount || 0),
      });
    } catch (e) {
      console.warn('[sendbird-client] refreshUnreadForUrl failed:', e);
    }
  };

  const refreshChannelMessages = async (channelUrl = null, limit = 30) => {
    const url = String(channelUrl || activeCollectionUrl.value || activeChannel.value?.url || '').trim();
    if (!url) return [];

    try {
      const channel = await getChannelWithRetry(url, { tries: 3, delayMs: 100 });
      if (!channel) return [];

      const list = await fallbackFetchRecentMessages(channel, limit);
      const normalized = await normalizeIncomingMessages(sortMessages(list || []));
      await writeSnapshot(url, normalized, { replace: true });

      if (isViewingChannel(channel) && Number(channel?.unreadMessageCount || 0) > 0) {
        try {
          await markChannelRead(channel);
        } catch {}
      }

      return cloneDeep(normalized);
    } catch (e) {
      console.warn('[sendbird-client] refreshChannelMessages failed:', e);
      return [];
    }
  };
  const resolveMessageUrl = (msg) => {
    const raw =
      msg?.plainUrl ||
      msg?.url ||
      msg?.secureUrl ||
      msg?.fileUrl ||
      (msg?.file && (msg.file.url || msg.file.localUrl)) ||
      '';

    if (!raw) return '';

    try {
      if (typeof sbInstance?.getFileUrl === 'function') {
        return sbInstance.getFileUrl(raw);
      }
      if (typeof sbInstance?.createFileUrl === 'function') {
        return sbInstance.createFileUrl(raw);
      }
      if (typeof sbInstance?.auth?.getFileUrl === 'function') {
        return sbInstance.auth.getFileUrl(raw);
      }
      if (typeof window?.SendbirdChat?.getFileUrl === 'function') {
        return window.SendbirdChat.getFileUrl(raw);
      }
    } catch (e) {
      console.warn('[sendbird-client] resolveMessageUrl signer failed:', e);
    }

    return raw;
  };

  const hydrateUnreadByUrls = async (urls = []) => {
    if (!sbInstance || !urls.length) return {};

    const q = sbInstance.groupChannel.createMyGroupChannelListQuery({
      includeEmpty: true,
      limit: urls.length,
      channelUrlsFilter: urls,
    });

    const list = await q.next();
    const map = {};
    for (const ch of list) {
      map[ch.url] = Number(ch.unreadMessageCount || 0);
    }
    return map;
  };

  const hydrateUnreadForRequestIds = async (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    const urls = ids.map(id => `group_channel_${id}`);
    try {
      const map = await hydrateUnreadByUrls(urls);
      for (const id of ids) {
        const url = `group_channel_${id}`;
        const count = Number(map[url] || 0);
        await store.dispatch('serviceRequests/updateRequestUnread', {
          id: String(id),
          count,
        });
      }
    } finally {
      setHasHydratedUnread(true);
    }
  };

  const getChannelList = async ({ ids = [], url = null } = {}) => {
    if (!sbInstance) await initSendbird();

    const urls = ids.length ? ids.map(id => `group_channel_${id}`) : url ? [url] : [];
    if (!urls.length) return [];

    const query = sbInstance.groupChannel.createMyGroupChannelListQuery({
      includeEmpty: true,
      limit: 50,
      channelUrlsFilter: urls
    });

    try {
      const channelList = await query.next();

      if (store.hasModule('serviceRequests')) {
        store.dispatch('serviceRequests/updateUnreadChats', channelList);
      }

      return channelList;
    } catch (error) {
      console.error('getChannelList error:', error);
      return [];
    }
  };

  const fetchChannelIfExists = async (channelUrl) => {
    if (!isSafeChannelUrl(channelUrl)) {
      console.warn(`DEBUG => [sendbird-client.js] => fetchChannelIfExists: Invalid channelUrl ${channelUrl}, skipping API call`);
      return null;
    }
    try {
      return await getChannelWithRetry(channelUrl, { tries: 4, delayMs: 120 });
    } catch (error) {
      if (error.code === 400404 || error.message?.includes('not found')) {
        console.info(`DEBUG => [sendbird-client] fetchChannelIfExists: Channel ${channelUrl} does not exist.`);
      } else if (error.code === 400) {
        console.info(`DEBUG => [sendbird-client] fetchChannelIfExists: Bad Request for ${channelUrl}, skipping.`);
      } else {
        console.error(`DEBUG => [sendbird-client] fetchChannelIfExists: Error fetching ${channelUrl}`, error);
      }
      return null;
    }
  };

  const getChannelByUrlSafe = async (channelUrl) => {
    if (!isSafeChannelUrl(channelUrl)) return null;
    try {
      return await getChannelWithRetry(channelUrl, { tries: 4, delayMs: 120 });
    } catch (e) {
      if (e?.code === 400404) return null;
      throw e;
    }
  };

  const ensureMembership = async (channel) => {
    const state = channel?.myMemberState;
    if (state === 'invited' && channel.acceptInvitation) {
      await channel.acceptInvitation();
      return channel;
    }
    if (state === 'none' && channel.join) {
      try {
        await channel.join();
        return channel;
      } catch (e) {
        console.warn('[sendbird-client] join() failed (likely private channel or permissions):', e);
        throw e;
      }
    }
    return channel;
  };

  const validateUser = async (channel) => {
    const currentUserId = store.getters['auth/userProfile'].email;
    const isMember = channel.members.some((m) => m.userId === currentUserId);

    if (isMember) {
      return channel;
    }

    try {
      return await channel.join();
    } catch (error) {
      const auditRec = {
        source: 'Portal3',
        userIds: (channel.members || []).map(m => m.userId).join(', '),
        channel: channel.url,
        details: `(validateUser) Error joining channel: ${error.message || error}`
      };
      console.error('Error (validateUser)', error);
      store.dispatch('sendbird/audit', auditRec);
      throw error;
    }
  };

  const createOrJoinChannel = async (data, skipFetch = false) => {
    if (!data?.channelUrl) throw new Error('Missing channelUrl');

    const me =
      store.getters['auth/chatIdentityEmail'] ||
      store.getters['auth/userProfile']?.email ||
      '';

    const extra = Array.isArray(data.userIds) ? data.userIds.filter(Boolean) : [];
    const invitedUserIds = Array.from(new Set([me, ...extra])).filter(Boolean);

    if (!skipFetch) {
      const existing = await fetchChannelIfExists(data.channelUrl);
      if (existing) {
        setActiveChannel(existing);
        await ensureMembership(existing);
        return await validateUser(existing);
      }
    }

    try {
      const params = {
        isDistinct: false,
        isPublic: true,
        isDiscoverable: true,
        channelUrl: data.channelUrl,
        name: data.name || '',
        invitedUserIds,
        customType: channelType(),
        data: (data.language ? [data.language, 'en'] : ['en']).join(','),
      };

      const newChannel = await sbInstance.groupChannel.createChannel(params);
      const ch = await sbInstance.groupChannel.getChannel(newChannel.url);
      setActiveChannel(ch);
      await ensureMembership(ch);
      return await validateUser(ch);
    } catch (error) {
      if (error.code === 400 && /channel_url|already exists/i.test(error.message || '')) {
        const existing = await fetchChannelIfExists(data.channelUrl);
        if (existing) {
          setActiveChannel(existing);
          await ensureMembership(existing);
          return await validateUser(existing);
        }
      }
      console.error(`[sendbird-client] createOrJoinChannel failed for ${data.channelUrl}`, error);
      throw error;
    }
  };

  const openOrCreateAndJoin = async (channelUrl, displayName, language) => {
    const info = await (async () => {
      const existing = await getChannelByUrlSafe(channelUrl);
      if (existing) {
        await ensureMembership(existing);
        setActiveChannel(existing);
        return getChannelInfo(existing);
      }
      const created = await createOrJoinChannel({
        channelUrl,
        name: displayName ?? channelUrl.replace('group_channel_', ''),
        userIds: [store.getters['auth/userProfile'].email],
        language
      });
      setActiveChannel(created);
      return getChannelInfo(created);
    })();

    if (info?.channel) {
      try {
        await openMessageCollection(info.channel);
      } catch (e) {
        console.warn('[sendbird-client] openMessageCollection failed in openOrCreateAndJoin, falling back to reconcile:', e);
        scheduleReconcile(channelUrl, 0);
      }
    }

    return info;
  };

  const channelExist = async (channelUrl) => {
    try {
      const channel = await getChannelWithRetry(channelUrl, { tries: 4, delayMs: 120 });
      setActiveChannel(channel);
      store.dispatch('sendbird/audit', {
        source: 'Portal3',
        userIds: channel.creator?.userId,
        channel: channel.url,
        details: '(channelExist) Exists',
      });
      return true;
    } catch (error) {
      const message = (() => {
        switch (error.code) {
          case 400404:
            return 'Channel does not exist.';
          case 400201:
            return 'Channel not found.';
          case 400300:
            return 'Session expired.';
          default:
            return `Unknown error: ${error.message}`;
        }
      })();

      store.dispatch('sendbird/audit', {
        source: 'Portal3',
        userIds: '',
        channel: channelUrl,
        details: `(channelExist) ${message}`,
      });
      return false;
    }
  };

  const sendMessage = async (text) => {
    const channel = activeChannel.value;
    if (!channel) throw new Error('No active channel');
    if (!text) throw new Error('Message text is empty');

    const uiLang = getUiLang();
    const languages = [uiLang, 'en'];

    const params = {
      message: text,
      customType: 'chat_text',
      translationTargetLanguages: languages,
      data: [uiLang, 'en'].join(','),
    };

    console.log(`DEBUG => [sendbird-client] Sending message in languages: ${languages.join(', ')}`);

    const audit = {
      source: 'Portal3',
      userIds: channel.inviter?.userId || 'unknown',
      channel: channel.url,
    };

    try {
      const finalMsg = await new Promise((resolve, reject) => {
        channel.sendUserMessage(params)
          .onSucceeded(async (message) => {
            const collectionOwnsThisChannel =
              !!activeCollection.value && activeCollectionUrl.value === channel.url;

            const normalizedMessage = await normalizeIncomingMessage(message);

            if (!collectionOwnsThisChannel) {
              try {
                const translated = await channel.translateUserMessage(message, languages);
                const cloned = cloneDeep(normalizedMessage);
                const target = languages[0];

                cloned.message =
                  translated?.translations?.[target] ??
                  translated?.translations?.[target?.split('-')[0]] ??
                  translated?.message ??
                  cloned.message;

                await store.dispatch('sendbird/addLiveMessage', {
                  channelUrl: channel.url,
                  message: cloned,
                });
              } catch {
                await store.dispatch('sendbird/addLiveMessage', {
                  channelUrl: channel.url,
                  message: normalizedMessage,
                });
              }
            }

            audit.details = `(sendMessage) ${normalizedMessage.message || text}`;
            await store.dispatch('sendbird/audit', audit);

            resolve(normalizedMessage);
          })
          .onFailed(async (error) => {
            console.error('DEBUG => Message send failed:', error);
            audit.details = `(sendMessage) Error: ${error.message}`;
            await store.dispatch('sendbird/audit', audit);
            reject(error);
          });
      });

      return finalMsg;
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  };

  const getMessageList = async (channelInfo) => {
    const channel = channelInfo?.channel;
    if (!channel) return [];

    const url = String(channel.url || '').trim();

    if (activeCollection.value && activeCollectionUrl.value === url) {
      const snapshot = getSnapshot(url);
      if (snapshot.length > 0) {
        return cloneDeep(snapshot.slice(-GET_MESSAGE_LIMIT));
      }
    }

    const cached = getSnapshot(url);
    if (cached.length > 0) {
      return cloneDeep(cached.slice(-GET_MESSAGE_LIMIT));
    }

    const list = await fallbackFetchRecentMessages(channel, GET_MESSAGE_LIMIT);
    const normalized = await normalizeIncomingMessages(sortMessages(list || []));
    await writeSnapshot(url, normalized, { replace: true });

    return cloneDeep(normalized);
  };

  const getAllMessages = async (channelUrl) => {
    const channel = await getChannelWithRetry(channelUrl, { tries: 4, delayMs: 120 });
    const query = channel.createPreviousMessageListQuery();
    let allMessages = [];

    while (query.hasNext && !query.isLoading) {
      const batch = await query.load(50, false);
      allMessages.push(...batch);
    }

    return await normalizeIncomingMessages(sortMessages(allMessages));
  };

  const loadPreviousMessages = async (channelUrl = null) => {
    const url = String(channelUrl || activeCollectionUrl.value || '').trim();
    if (!url) return [];

    if (!(activeCollection.value && activeCollectionUrl.value === url)) {
      const channel = await getChannelWithRetry(url, { tries: 3, delayMs: 100 });
      await openMessageCollection(channel);
    }

    if (!activeCollection.value?.hasPrevious) {
      return [];
    }

    const older = await activeCollection.value.loadPrevious();
    const list = await normalizeIncomingMessages(sortMessages(older || []));
    await writeSnapshot(url, list, { replace: false });

    return cloneDeep(list);
  };

  const uploadFile = async (file) => {
    const channel = activeChannel.value;
    if (!channel) throw new Error('No active channel');
    if (!file) throw new Error('File missing');

    const params = {
      file,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      customType: 'chat_attachment',
    };

    try {
      const finalMessage = await new Promise((resolve, reject) => {
        channel.sendFileMessage(params)
          .onSucceeded(async (message) => {
            try {
              const collectionOwnsThisChannel =
                !!activeCollection.value && activeCollectionUrl.value === channel.url;

              // Keep the immediate message so the user sees something straight away.
              const normalizedMessage = await normalizeIncomingMessage(message);

              if (!collectionOwnsThisChannel) {
                await store.dispatch('sendbird/addLiveMessage', {
                  channelUrl: channel.url,
                  message: normalizedMessage,
                });
              }

              // IMPORTANT:
              // File messages often arrive partially hydrated in the live event.
              // Immediately re-fetch recent server messages so the image/video
              // message is replaced with the fully hydrated version without the user
              // having to press Refresh.
              setTimeout(async () => {
                try {
                  const recent = await fallbackFetchRecentMessages(channel, 30);
                  const normalizedRecent = await normalizeIncomingMessages(recent || []);
                  await writeSnapshot(channel.url, normalizedRecent, { replace: true });
                } catch (rehydrateErr) {
                  console.warn('[sendbird-client] post-upload rehydrate failed:', rehydrateErr);
                }
              }, 350);

              resolve(normalizedMessage);
            } catch (innerErr) {
              reject(innerErr);
            }
          })
          .onFailed((error) => {
            console.error('DEBUG => File message send failed:', error);
            reject(error);
          });
      });

      return finalMessage;
    } catch (e) {
      console.error('uploadFile error:', e);
      throw e;
    }
  };

  const getMessageTime = (message) => {
    if (!message?.createdAt) return '';
    const date = new Date(message.createdAt);
    const now = new Date();

    const pad = (n) => (n < 10 ? '0' + n : n);
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

    const isToday = now.toDateString() === date.toDateString();
    const isYesterday =
      now.getFullYear() === date.getFullYear() &&
      now.getMonth() === date.getMonth() &&
      now.getDate() - date.getDate() === 1;

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const suffix = (d) => (d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : `${d}th`);

    if (isToday) return `today at ${time}`;
    if (isYesterday) return `yesterday at ${time}`;
    return `${months[date.getMonth()]} ${suffix(date.getDate())} at ${time}`;
  };

  const getTotalUnreadCount = async () => {
    if (!sbInstance) return 0;
    const count = await sbInstance.groupChannel.getTotalUnreadMessageCount();
    store.dispatch('serviceRequests/totalUnreadChats', count);
    return count;
  };

  const subscribeToChannelMessages = (channel, callback) => {
    if (!channel || !sbInstance) return;

    const handler = new GroupChannelHandler();
    handler.onMessageReceived = async (ch, message) => {
      const normalizedMessage = await normalizeIncomingMessage(message);
      console.log('DEBUG => [sendbird-client] => subscribeToChannelMessages => onMessageReceived message: ', normalizedMessage);
      callback(normalizedMessage);
    };

    sbInstance.groupChannel.addGroupChannelHandler(channel.url, handler);
  };

  const unsubscribeFromChannelMessages = (channel) => {
    if (channel && sbInstance) {
      sbInstance.groupChannel.removeGroupChannelHandler(channel.url);
      console.log('Unsubscribed from channel:', channel.url);
    }
  };

  return {
    initSendbird,
    sendbirdConnect,
    getCurrentUser,
    disconnect,

    // Channel
    createOrJoinChannel,
    getChannelInfo,
    getChannelList,
    channelExist,
    setActiveChannel,
    getActiveChannel,
    fetchChannelIfExists,
    getMyChannelByUrl,
    getUnreadCountByUrls,
    getChannelByUrlSafe,
    ensureMembership,
    hydrateUnreadByUrls,
    hydrateUnreadForRequestIds,
    refreshUnreadForUrl,
    markChannelRead,

    // Messaging
    sendMessage,
    uploadFile,
    getMessageList,
    getAllMessages,
    getMessageTime,
    resolveMessageUrl,
    subscribeToChannelMessages,
    unsubscribeFromChannelMessages,
    openOrCreateAndJoin,
    requestIdFromChannel,
    setHasHydratedUnread,
    setChatOpen,
    finalizeReadAndClose,
    getViewingUrl,
    hydrateBadgeForRequest,
    loadPreviousMessages,
    openMessageCollection,
    disposeActiveCollection,
    refreshChannelMessages,

    // Unread
    getTotalUnreadCount,
  };
};
